export class VideoSourceManager {
    constructor() {
        this.currentStream = null;
        this.videoElement = null;
        this.availableDevices = [];
        this.onVideoLoadedCallback = null;
        this.onErrorCallback = null;
        this.onCameraChangeCallback = null; // Callback pour notifier les changements de caméra
        this.onVideoSourceChangeCallback = null; // Callback pour notifier les changements de source vidéo
        this.onFlipStateChangeCallback = null; // Callback pour notifier les changements de flip automatique
        this.currentCameraFacingMode = null; // 'user' pour front, 'environment' pour back, null pour autres sources
        this.currentVideoSourceType = 'webcam'; // 'webcam', 'file', 'url'
        this.savedStreamState = null; // Pour l'approche agressive de libération/redémarrage
        this.autoFlipEnabled = true; // Active le flip automatique pour les caméras front
        
        // Ajouter le listener beforeunload pour libérer la caméra
        this.setupBeforeUnloadHandler();
    }

    /**
     * Configure le gestionnaire beforeunload pour libérer la caméra
     */
    setupBeforeUnloadHandler() {
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                console.log('🧹 beforeunload: Libération des ressources caméra...');
                this.releaseCamera();
            });
            
            // Gestion uniquement avec visibilitychange (plus fiable sur mobile)
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    console.log('📱 Page cachée: Libération complète du flux caméra...');
                    this.saveStreamStateAndRelease();
                } else {
                    console.log('📱 Page visible: Redémarrage complet du flux caméra...');
                    this.restoreStreamFromState();
                }
            });
        }
    }

    /**
     * Met en pause le flux caméra sans le libérer complètement
     */
    pauseCameraStream() {
        try {
            if (this.currentStream && this.videoElement) {
                // Sauvegarder les paramètres actuels
                this.pausedStreamInfo = {
                    deviceId: this.currentStream.getVideoTracks()[0]?.getSettings()?.deviceId,
                    constraints: this.lastUsedConstraints || {},
                    facingMode: this.currentCameraFacingMode
                };
                
                // Mettre en pause la vidéo mais garder le stream
                this.videoElement.pause();
                console.log('⏸️ Flux caméra mis en pause');
            }
        } catch (error) {
            console.warn('Erreur lors de la pause caméra:', error);
        }
    }

    /**
     * Reprend le flux caméra si il était en pause
     */
    async resumeCameraStream() {
        try {
            if (this.pausedStreamInfo && this.videoElement) {
                console.log('▶️ Tentative de reprise du flux caméra...');
                
                // Si le stream existe encore et fonctionne
                if (this.currentStream) {
                    const tracks = this.currentStream.getVideoTracks();
                    if (tracks.length > 0 && tracks[0].readyState === 'live') {
                        // Le stream est toujours actif, juste reprendre la lecture
                        this.videoElement.play();
                        console.log('✅ Flux caméra repris (stream existant)');
                        this.pausedStreamInfo = null;
                        return;
                    }
                }
                
                // Le stream n'est plus valide, en recréer un
                console.log('🔄 Stream non valide, redémarrage...');
                const { deviceId, constraints, facingMode } = this.pausedStreamInfo;
                
                // Redémarrer avec les mêmes paramètres
                try {
                    await this.startWebcam(deviceId, constraints);
                    console.log('✅ Flux caméra redémarré avec succès');
                } catch (error) {
                    console.warn('⚠️ Impossible de redémarrer automatiquement:', error.message);
                    // Ne pas afficher d'alerte ici car c'est automatique
                }
                
                this.pausedStreamInfo = null;
            }
        } catch (error) {
            console.warn('Erreur lors de la reprise caméra:', error);
        }
    }

    /**
     * Sauvegarde l'état du stream et libère complètement la caméra (approche agressive)
     */
    saveStreamStateAndRelease() {
        try {
            if (this.currentStream && this.videoElement) {
                // Sauvegarder tous les paramètres nécessaires pour redémarrer
                this.savedStreamState = {
                    deviceId: this.currentStream.getVideoTracks()[0]?.getSettings()?.deviceId,
                    constraints: this.lastUsedConstraints || {},
                    facingMode: this.currentCameraFacingMode,
                    wasActive: true
                };
                
                console.log('💾 État du stream sauvegardé:', this.savedStreamState);
                
                // Libération complète du stream
                this.currentStream.getTracks().forEach(track => {
                    track.stop();
                    console.log('🔄 Track libéré (aggressif):', track.label);
                });
                this.currentStream = null;
                
                // Nettoyer l'élément vidéo
                if (this.videoElement) {
                    this.videoElement.srcObject = null;
                    this.videoElement.pause();
                }
            }
        } catch (error) {
            console.warn('Erreur lors de la sauvegarde/libération:', error);
        }
    }

    /**
     * Restore le stream depuis l'état sauvegardé (redémarrage complet)
     */
    async restoreStreamFromState() {
        try {
            if (this.savedStreamState && this.savedStreamState.wasActive) {
                console.log('🔄 Redémarrage complet du stream depuis l\'état sauvegardé...');
                
                const { deviceId, constraints, facingMode } = this.savedStreamState;
                
                try {
                    await this.startWebcam(deviceId, constraints);
                    console.log('✅ Stream redémarré avec succès (complet)');
                } catch (error) {
                    console.warn('⚠️ Impossible de redémarrer le stream:', error.message);
                    // Ne pas afficher d'alerte car c'est automatique
                }
                
                // Nettoyer l'état sauvegardé
                this.savedStreamState = null;
            }
        } catch (error) {
            console.warn('Erreur lors de la restauration du stream:', error);
        }
    }

    /**
     * Libère rapidement les ressources caméra (synchrone pour beforeunload)
     */
    releaseCamera() {
        try {
            if (this.currentStream) {
                this.currentStream.getTracks().forEach(track => {
                    track.stop();
                    console.log('🔄 Track libéré:', track.label);
                });
                this.currentStream = null;
            }
            
            if (this.videoElement) {
                this.videoElement.srcObject = null;
                this.videoElement.pause();
            }
        } catch (error) {
            console.warn('Erreur lors de la libération caméra:', error);
        }
    }

    /**
     * Initialise le gestionnaire avec un élément vidéo
     */
    init(videoElement) {
        this.videoElement = videoElement;
        return this;
    }

    /**
     * Définit le callback appelé quand la vidéo est chargée
     */
    onVideoLoaded(callback) {
        this.onVideoLoadedCallback = callback;
        return this;
    }

    /**
     * Définit le callback appelé en cas d'erreur
     */
    onError(callback) {
        this.onErrorCallback = callback;
        return this;
    }

    /**
     * Définit le callback appelé lors d'un changement de caméra
     */
    onCameraChange(callback) {
        this.onCameraChangeCallback = callback;
        return this;
    }

    /**
     * Définit le callback appelé lors d'un changement de source vidéo
     */
    onVideoSourceChange(callback) {
        this.onVideoSourceChangeCallback = callback;
        return this;
    }

    /**
     * Définit le callback appelé lors d'un changement d'état de flip automatique
     */
    onFlipStateChange(callback) {
        this.onFlipStateChangeCallback = callback;
        return this;
    }

    /**
     * Active ou désactive le flip automatique
     */
    setAutoFlip(enabled) {
        const previousState = this.autoFlipEnabled;
        this.autoFlipEnabled = enabled;
        
        // Notifier le changement si l'état a changé
        if (previousState !== enabled) {
            console.log(`🔄 Flip automatique ${enabled ? 'activé' : 'désactivé'}`);
            this.notifyFlipStateChange();
        }
        
        return this;
    }

    /**
     * Vérifie si le flip automatique est activé
     */
    isAutoFlipEnabled() {
        return this.autoFlipEnabled;
    }

    /**
     * Détermine si le flip doit être activé automatiquement
     * basé sur le type de caméra et les paramètres
     */
    shouldAutoFlip() {
        if (!this.autoFlipEnabled) {
            return false;
        }
        
        // Auto-flip uniquement pour les caméras front (selfie)
        // Pas de flip pour les caméras arrière ou les fichiers/URLs
        return this.currentCameraFacingMode === 'user';
    }

    /**
     * Notifie le changement d'état de flip aux callbacks
     */
    notifyFlipStateChange() {
        if (this.onFlipStateChangeCallback) {
            const shouldFlip = this.shouldAutoFlip();
            this.onFlipStateChangeCallback(shouldFlip, this.currentCameraFacingMode);
        }
    }

    /**
     * Récupère le type de source vidéo actuelle
     */
    getCurrentVideoSourceType() {
        return this.currentVideoSourceType;
    }

    /**
     * Récupère la liste des périphériques vidéo disponibles
     */
    async getAvailableDevices() {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            this.availableDevices = devices.filter(device => device.kind === 'videoinput');
            return this.availableDevices.map(device => ({
                deviceId: device.deviceId,
                label: device.label || `Caméra ${device.deviceId.substring(0, 8)}...`,
                groupId: device.groupId
            }));
        } catch (error) {
            console.error('Erreur lors de la récupération des dispositifs:', error);
            if (this.onErrorCallback) this.onErrorCallback(error);
            return [];
        }
    }    /**
     * Détecte le mode de facing de la caméra
     */
    async detectCameraFacingMode(deviceId) {
        try {
            // Récupérer les informations détaillées des dispositifs
            const devices = await navigator.mediaDevices.enumerateDevices();
            const targetDevice = devices.find(device => device.deviceId === deviceId);
            
            if (targetDevice && targetDevice.label) {
                const label = targetDevice.label.toLowerCase();
                console.log(`🔍 Détection caméra pour: "${targetDevice.label}"`);
                
                // Détection basée sur le nom de la caméra
                // Mots-clés pour caméra avant (selfie)
                if (label.includes('front') || label.includes('user') || label.includes('selfie') || 
                    label.includes('facetime') || label.includes('internal') || label.includes('intégré') ||
                    label.includes('built-in') || label.includes('interne') || label.includes('avant') ||
                    (label.includes('webcam') && !label.includes('usb') && !label.includes('external'))) {
                    console.log('📱 → Détecté: Caméra AVANT (selfie) - Flip automatique activé');
                    return 'user';
                } 
                // Mots-clés pour caméra arrière
                else if (label.includes('back') || label.includes('rear') || label.includes('environment') || 
                         label.includes('external') || label.includes('usb') || label.includes('capture') ||
                         label.includes('arrière') || label.includes('principale')) {
                    console.log('📷 → Détecté: Caméra ARRIÈRE - Flip automatique désactivé');
                    return 'environment';
                }
            }
            
            // Si pas de deviceId spécifique, essayer de tester avec les contraintes
            if (!deviceId) {
                // console.log('🔍 Test des contraintes facingMode...');
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    const errMsg = 'Erreur: getUserMedia non supporté sur ce navigateur. Veuillez utiliser un navigateur compatible.';
                    console.error(errMsg);
                    if (this.onErrorCallback) this.onErrorCallback(errMsg);
                    return null;
                }
                try {
                    // Tester d'abord avec facingMode: 'user'
                    const testStream = await navigator.mediaDevices.getUserMedia({
                        video: { facingMode: 'user' }
                    });
                    testStream.getTracks().forEach(track => track.stop());
                    // console.log('📱 → Test réussi: Caméra AVANT disponible - Flip automatique activé');
                    return 'user';
                } catch {
                    try {
                        // Tester avec facingMode: 'environment'
                        const testStream = await navigator.mediaDevices.getUserMedia({
                            video: { facingMode: 'environment' }
                        });
                        testStream.getTracks().forEach(track => track.stop());
                        // console.log('📷 → Test réussi: Caméra ARRIÈRE disponible - Flip automatique désactivé');
                        return 'environment';
                    } catch {
                        // console.log('❓ → Aucune contrainte facingMode supportée');
                        return null;
                    }
                }
            }
            
            console.log('❓ → Type de caméra non déterminé - Comportement par défaut');
            return null;
        } catch (error) {
            console.warn('⚠️ Erreur lors de la détection de caméra:', error);
            return null;
        }
    }/**
     * Démarre la capture depuis une webcam
     */
    async startWebcam(deviceId = null, constraints = {}) {
        try {
            // Arrêter le flux précédent s'il existe
            await this.stop();

            // Sauvegarder les contraintes pour pouvoir redémarrer plus tard
            this.lastUsedConstraints = constraints;
            this.lastUsedDeviceId = deviceId;

            // Détecter le mode de facing de la caméra
            this.currentCameraFacingMode = await this.detectCameraFacingMode(deviceId);

            const videoConstraints = {
                ...constraints
            };

            if (deviceId) {
                videoConstraints.deviceId = { exact: deviceId };
            }

            const mediaConstraints = {
                audio: false,
                video: videoConstraints
            };

            // Ajouter un timeout pour détecter si la caméra reste bloquée
            const getUserMediaWithTimeout = () => {
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    const errMsg = 'Erreur: getUserMedia non supporté sur ce navigateur. Veuillez utiliser un navigateur compatible.';
                    console.error(errMsg);
                    if (this.onErrorCallback) this.onErrorCallback(errMsg);
                    return Promise.reject(new Error(errMsg));
                }
                return Promise.race([
                    navigator.mediaDevices.getUserMedia(mediaConstraints),
                    new Promise((_, reject) => 
                        setTimeout(() => reject(new Error('TIMEOUT')), 5000)
                    )
                ]);
            };

            this.currentStream = await getUserMediaWithTimeout();
            this.videoElement.srcObject = this.currentStream;

            return new Promise((resolve, reject) => {
                // Timeout pour l'élément vidéo
                const videoTimeout = setTimeout(() => {
                    alert('⚠️ Caméra occupée!\n\nLa caméra est utilisée par une autre application ou onglet.\n\nSur mobile, cela arrive souvent avec plusieurs instances.\n\nEssayez de:\n• Fermer les autres onglets\n• Actualiser la page\n• Redémarrer le navigateur');
                    reject(new Error('VIDEO_TIMEOUT'));
                }, 5000); // 5 secondes pour l'élément vidéo

                this.videoElement.onloadeddata = () => {
                    clearTimeout(videoTimeout);
                    const width = this.videoElement.videoWidth;
                    const height = this.videoElement.videoHeight;
                    
                    // Mettre à jour le type de source
                    this.currentVideoSourceType = 'webcam';
                    
                    if (this.onVideoLoadedCallback) {
                        this.onVideoLoadedCallback(width, height);
                    }
                    
                    // Notifier le changement de caméra
                    if (this.onCameraChangeCallback) {
                        this.onCameraChangeCallback(this.currentCameraFacingMode);
                    }
                    
                    // Notifier le changement de source vidéo
                    if (this.onVideoSourceChangeCallback) {
                        this.onVideoSourceChangeCallback('webcam');
                    }
                    
                    // Notifier le changement d'état de flip automatique
                    this.notifyFlipStateChange();
                    
                    resolve();
                };

                this.videoElement.onerror = (error) => {
                    clearTimeout(videoTimeout);
                    if (this.onErrorCallback) this.onErrorCallback(error);
                    reject(error);
                };
            });

        } catch (error) {
            // Alerte simple pour les problèmes de flux webcam
            if (error.message === 'TIMEOUT') {
                alert('⏱️ Timeout caméra!\n\nLa caméra met trop de temps à répondre.\n\nCela arrive souvent quand:\n• La caméra est occupée par une autre app/onglet\n• Le système est surchargé\n\nEssayez de:\n• Fermer les autres onglets\n• Actualiser la page\n• Redémarrer le navigateur');
            } else if (error.message === 'VIDEO_TIMEOUT') {
                // Déjà géré dans le timeout de l'élément vidéo
            } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                alert('⚠️ Caméra occupée!\n\nLa caméra est probablement utilisée par une autre application ou onglet.\n\nEssayez de:\n• Fermer les autres onglets\n• Actualiser la page\n• Redémarrer le navigateur');
            } else if (error.name === 'NotAllowedError') {
                alert('❌ Accès refusé!\n\nVeuillez autoriser l\'accès à la caméra.');
            } else if (error.name === 'NotFoundError') {
                alert('📷 Caméra introuvable!\n\nAucune caméra détectée sur cet appareil.');
            } else {
                alert('❌ Erreur caméra!\n\n' + error.message);
            }
            
            if (this.onErrorCallback) this.onErrorCallback(error);
            throw error;
        }
    }    /**
     * Charge une vidéo depuis un fichier
     */
    async loadVideoFile(file) {
        try {
            // Arrêter le flux précédent s'il existe
            await this.stop();

            // Réinitialiser le mode de facing car ce n'est pas une webcam
            this.currentCameraFacingMode = null;

            // Vérifier que c'est bien un fichier vidéo
            if (!file.type.startsWith('video/')) {
                throw new Error('Le fichier sélectionné n\'est pas une vidéo');
            }

            const url = URL.createObjectURL(file);
            this.videoElement.src = url;
            this.videoElement.srcObject = null;
            this.videoElement.loop = true; // Active la lecture en boucle pour les fichiers vidéo

            return new Promise((resolve, reject) => {
                this.videoElement.onloadeddata = async () => {
                    const width = this.videoElement.videoWidth;
                    const height = this.videoElement.videoHeight;
                    
                    // Mettre à jour le type de source
                    this.currentVideoSourceType = 'file';
                    
                    // Démarrer la lecture de la vidéo
                    try {
                        await this.videoElement.play();
                    } catch (playError) {
                        console.warn('⚠️ Autoplay bloqué, la vidéo nécessite une interaction utilisateur:', playError.message);
                    }
                    
                    if (this.onVideoLoadedCallback) {
                        this.onVideoLoadedCallback(width, height);
                    }
                    
                    // Notifier le changement de source vidéo
                    if (this.onVideoSourceChangeCallback) {
                        this.onVideoSourceChangeCallback('file');
                    }
                    
                    // Notifier que le flip doit être désactivé (pas de caméra front)
                    this.notifyFlipStateChange();
                    
                    resolve();
                };

                this.videoElement.onerror = (error) => {
                    URL.revokeObjectURL(url);
                    if (this.onErrorCallback) this.onErrorCallback(error);
                    reject(error);
                };                // Nettoyer l'URL quand la vidéo est terminée ou quand on change de source
                this.videoElement.onended = () => {
                    if (this.videoElement.src && this.videoElement.src.startsWith('blob:')) {
                        URL.revokeObjectURL(this.videoElement.src);
                        console.log('🧹 URL blob révoquée après fin de vidéo');
                    }
                };
            });

        } catch (error) {
            console.error('Erreur lors du chargement du fichier vidéo:', error);
            if (this.onErrorCallback) this.onErrorCallback(error);
            throw error;
        }
    }    /**
     * Charge une vidéo depuis une URL
     */
    async loadVideoUrl(url) {
        try {
            // Arrêter le flux précédent s'il existe
            await this.stop();

            // Réinitialiser le mode de facing car ce n'est pas une webcam
            this.currentCameraFacingMode = null;

            this.videoElement.src = url;
            this.videoElement.srcObject = null;

            return new Promise((resolve, reject) => {
                this.videoElement.onloadeddata = async () => {
                    const width = this.videoElement.videoWidth;
                    const height = this.videoElement.videoHeight;
                    
                    // Mettre à jour le type de source
                    this.currentVideoSourceType = 'url';
                    
                    // Démarrer la lecture de la vidéo
                    try {
                        await this.videoElement.play();
                    } catch (playError) {
                        console.warn('⚠️ Autoplay bloqué, la vidéo nécessite une interaction utilisateur:', playError.message);
                    }
                    
                    if (this.onVideoLoadedCallback) {
                        this.onVideoLoadedCallback(width, height);
                    }
                    
                    // Notifier le changement de source vidéo
                    if (this.onVideoSourceChangeCallback) {
                        this.onVideoSourceChangeCallback('url');
                    }
                    
                    // Notifier que le flip doit être désactivé (pas de caméra front)
                    this.notifyFlipStateChange();
                    
                    resolve();
                };

                this.videoElement.onerror = (error) => {
                    if (this.onErrorCallback) this.onErrorCallback(error);
                    reject(error);
                };
            });

        } catch (error) {
            console.error('Erreur lors du chargement de l\'URL vidéo:', error);
            if (this.onErrorCallback) this.onErrorCallback(error);
            throw error;
        }
    }    /**
     * Arrête le flux vidéo actuel
     */
    async stop() {
        // Arrêter le flux de la webcam
        if (this.currentStream) {
            this.currentStream.getTracks().forEach(track => {
                track.stop();
                // console.log('🔄 Track vidéo arrêté:', track.label);
            });
            this.currentStream = null;
        }

        // Nettoyer l'élément vidéo
        if (this.videoElement) {
            // Pause et reset de la vidéo
            this.videoElement.pause();
            this.videoElement.srcObject = null;
            
            // Nettoyer les URLs créées pour les fichiers
            if (this.videoElement.src && this.videoElement.src.startsWith('blob:')) {
                URL.revokeObjectURL(this.videoElement.src);
                console.log('🧹 URL blob révoquée');
            }
            
            this.videoElement.src = '';
            this.videoElement.load(); // Force le rechargement pour nettoyer
        }

        // Réinitialiser le mode de facing
        this.currentCameraFacingMode = null;
        
        // Nettoyer les informations de pause
        this.pausedStreamInfo = null;
        this.lastUsedConstraints = null;
        this.lastUsedDeviceId = null;
       
    }

    /**
     * Récupère le mode de facing de la caméra actuelle
     */
    getCurrentCameraFacingMode() {
        return this.currentCameraFacingMode;
    }

    /**
     * Récupère le type de source vidéo actuelle
     */
    getCurrentVideoSourceType() {
        return this.currentVideoSourceType;
    }

    /**
     * Vérifie si la caméra actuelle est une caméra avant (selfie)
     */
    isFrontCamera() {
        return this.currentCameraFacingMode === 'user';
    }

    /**
     * Vérifie si la caméra actuelle est une caméra arrière
     */
    isBackCamera() {
        return this.currentCameraFacingMode === 'environment';
    }

    /**
     * Vérifie si une source vidéo est active
     */
    isActive() {
        return this.currentStream !== null || 
               (this.videoElement && this.videoElement.src && !this.videoElement.paused);
    }

    /**
     * Obtient des informations détaillées sur l'état du flip automatique
     */
    getFlipInfo() {
        return {
            isAutoFlipEnabled: this.autoFlipEnabled,
            currentFacingMode: this.currentCameraFacingMode,
            shouldFlip: this.shouldAutoFlip(),
            facingModeText: this.currentCameraFacingMode === 'user' ? 'Avant (Front)' : 
                           this.currentCameraFacingMode === 'environment' ? 'Arrière (Back)' : 
                           'Inconnue/Fichier',
            sourceType: this.currentVideoSourceType
        };
    }

    /**
     * Obtient les informations sur la source vidéo actuelle
     */
    getCurrentSourceInfo() {
        if (!this.videoElement) return null;

        return {
            width: this.videoElement.videoWidth,
            height: this.videoElement.videoHeight,
            isWebcam: this.currentStream !== null,
            isFile: this.videoElement.src && !this.currentStream,
            duration: this.videoElement.duration,
            currentTime: this.videoElement.currentTime
        };
    }

    /**
     * Crée une interface utilisateur pour sélectionner la source vidéo
     */
    async createSourceSelector(container) {
        // Créer l'interface
        const selectorDiv = document.createElement('div');
        selectorDiv.className = 'video-source-selector';
        selectorDiv.style.cssText = `
            background: rgba(0,0,0,0.8);
            padding: 20px;
            border-radius: 10px;
            color: white;
            margin: 10px;
            font-family: Arial, sans-serif;
        `;

        const title = document.createElement('h3');
        title.textContent = 'Sélectionner une source vidéo';
        title.style.marginTop = '0';
        selectorDiv.appendChild(title);

        // Section Webcam
        const webcamSection = document.createElement('div');
        webcamSection.style.marginBottom = '20px';
        
        const webcamTitle = document.createElement('h4');
        webcamTitle.textContent = 'Webcam';
        webcamSection.appendChild(webcamTitle);

        const webcamSelect = document.createElement('select');
        webcamSelect.style.cssText = 'width: 200px; margin-right: 10px; padding: 5px;';
        
        const webcamButton = document.createElement('button');
        webcamButton.textContent = 'Démarrer Webcam';
        webcamButton.style.cssText = 'padding: 5px 10px; background: #4CAF50; color: white; border: none; border-radius: 3px; cursor: pointer;';

        webcamSection.appendChild(webcamSelect);
        webcamSection.appendChild(webcamButton);
        selectorDiv.appendChild(webcamSection);

        // Section Fichier
        const fileSection = document.createElement('div');
        fileSection.style.marginBottom = '20px';
        
        const fileTitle = document.createElement('h4');
        fileTitle.textContent = 'Fichier vidéo';
        fileSection.appendChild(fileTitle);

        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'video/*';
        fileInput.style.cssText = 'margin-right: 10px;';

        const fileButton = document.createElement('button');
        fileButton.textContent = 'Charger Fichier';
        fileButton.style.cssText = 'padding: 5px 10px; background: #2196F3; color: white; border: none; border-radius: 3px; cursor: pointer;';

        fileSection.appendChild(fileInput);
        fileSection.appendChild(fileButton);
        selectorDiv.appendChild(fileSection);

        // Section URL
        const urlSection = document.createElement('div');
        urlSection.style.marginBottom = '20px';
        
        const urlTitle = document.createElement('h4');
        urlTitle.textContent = 'URL vidéo';
        urlSection.appendChild(urlTitle);

        const urlInput = document.createElement('input');
        urlInput.type = 'url';
        urlInput.placeholder = 'https://example.com/video.mp4';
        urlInput.style.cssText = 'width: 300px; margin-right: 10px; padding: 5px;';

        const urlButton = document.createElement('button');
        urlButton.textContent = 'Charger URL';
        urlButton.style.cssText = 'padding: 5px 10px; background: #FF9800; color: white; border: none; border-radius: 3px; cursor: pointer;';

        urlSection.appendChild(urlInput);
        urlSection.appendChild(urlButton);
        selectorDiv.appendChild(urlSection);

        // Bouton Stop
        const stopButton = document.createElement('button');
        stopButton.textContent = 'Arrêter';
        stopButton.style.cssText = 'padding: 5px 10px; background: #f44336; color: white; border: none; border-radius: 3px; cursor: pointer; margin-top: 10px;';
        selectorDiv.appendChild(stopButton);

        container.appendChild(selectorDiv);

        // Charger les webcams disponibles
        const devices = await this.getAvailableDevices();
        webcamSelect.innerHTML = '<option value="">Sélectionner une webcam...</option>';
        devices.forEach(device => {
            const option = document.createElement('option');
            option.value = device.deviceId;
            option.textContent = device.label;
            webcamSelect.appendChild(option);
        });

        // Event listeners
        webcamButton.onclick = () => {
            const deviceId = webcamSelect.value;
            if (deviceId) {
                this.startWebcam(deviceId);
            } else {
                this.startWebcam();
            }
        };

        fileButton.onclick = () => {
            const file = fileInput.files[0];
            if (file) {
                this.loadVideoFile(file);
            }
        };

        urlButton.onclick = () => {
            const url = urlInput.value.trim();
            if (url) {
                this.loadVideoUrl(url);
            }
        };

        stopButton.onclick = () => {
            this.stop();
        };

        return selectorDiv;
    }
}

